/* Copyright (c) 2020-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, ErrorHandler } from '@angular/core';
import { Observable, of, Subscription, ReplaySubject, throwError, noop, timer, combineLatest } from 'rxjs';
import {
    mergeMap,
    catchError,
    map,
    tap,
    filter,
    first,
    shareReplay,
    finalize,
    take,
    retryWhen,
    switchMap
} from 'rxjs/operators';
import { isNil, isEqual, isEmpty, cloneDeep } from 'lodash';
import { Commands } from 'bridge-commands/commands';
import { Types } from 'bridge-commands/types';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { LocaleService } from 'crimson/lib/locale-service/locale.service';
import { NetworkStatusService } from 'crimson/lib/network-status-service/network-status.service';
import { SystemInfoService } from 'crimson/lib/system-info-service/system-info.service';
import { SystemInfoKey } from 'crimson/lib/system-info-service/system-info.models';
import { DbService } from 'crimson/lib/db/db.service';
import { DbStore } from 'crimson/lib/db/models/db.models';
import { SharedStorageService } from 'shared-storage/lib/shared-storage.service';
import { SingleEventStream } from 'utils/lib/event-stream/event-streams';
import { SingleEventStreamFactoryService } from 'utils/lib/event-stream-factory/event-stream-factory.service';
import { isTimeoutOrConnectivityError } from 'utils/lib/common/common-utils';
import { BaseConfigType } from 'app-config/lib/app.config';
import { OpenTelemetrySpan } from 'distributed-tracing/lib/distributed-tracing';
import {
    STARFLEET_LOGOUT_URL_TEMPLATE,
    STARFLEET_LOGOUT_URL_TEMPLATE_SAME_TAB,
    StarfleetStoreDetails,
    StorageType
} from '../constants/constants';
import { Session } from '../models/session.model';
import { StarfleetConfig } from '../starfleet-config';
import {
    StarfleetTransitionState,
    StarfleetUser,
    AccessTokenResponse,
    ClientTokenResponse,
    OAuthLoginData,
    KVStoreClientDataResponse,
    AuthErrors,
    ErrorInfo,
    SessionData,
    DeviceAuthorizeResponse,
    DeviceDisplayCodeData,
    IdTokenData,
    AuthStatus,
    LoginOptions
} from '../starfleet.interface';
import { StarfleetService } from '../starfleet.service';
import { OAuthService } from './o-auth.service';
import { StarfleetHttpService } from './starfleet-http.service';

const markLogoutInitiateKey = 'markLogoutInitiated';
const markLogoutInitiateVal = 'Logout Initiated';
const logoutNotSupportedUrlKey = 'logoutNotSupportedUrl';

@Injectable({
    providedIn: 'root'
})
export class StarfleetServiceDesktop extends StarfleetService {
    logger: Logger;
    private starfleetUserSubscription: SingleEventStream<StarfleetUser>;
    private session: Session;
    private storageType: StorageType = StorageType.SharedStorage; // Default to SharedStorage
    private globalStore: DbStore;
    readonly SAFE_RETRY_COUNT = 5;
    private initSub = new ReplaySubject<boolean>(1);
    private loginSubject: ReplaySubject<StarfleetTransitionState>;
    private logoutSubject: ReplaySubject<StarfleetTransitionState>;
    private authStatusSubject = new ReplaySubject<AuthStatus>(1);
    private loginSubscription: Subscription = null;
    private sessionSubject: ReplaySubject<Session>;
    private deviceIdSub: Observable<string> = null;
    private useCachedUser = false;
    private firstRefreshDone = false;
    private errorInfo: ErrorInfo = { error_code: '', error_description: '', error_uri: '' };
    private isSessionRefreshable = true;
    private isNextClientTokenRefreshAllowed = true;
    private useDeviceAuthLogin: boolean;
    private deviceDisplayCode: DeviceDisplayCodeData = null;
    private skipOpeningLogoutUrl = false;
    private isSameTabLoginFeatureEnabled = false;
    private idTokenRefreshUnderDuration = 0;

    constructor(
        private config: StarfleetConfig,
        loggingService: LoggingService,
        private starfleetHttpService: StarfleetHttpService,
        private oAuthService: OAuthService,
        private sharedStorage: SharedStorageService,
        private localeService: LocaleService,
        eventStreamFactory: SingleEventStreamFactoryService,
        private networkStatus: NetworkStatusService,
        private systemInfoService: SystemInfoService,
        private errorHandler: ErrorHandler,
        private bridgeService: BridgeService,
        private dbService: DbService
    ) {
        super();
        this.logger = loggingService.getLogger('starfleet/desktop');
        this.logger.info('constructing');

        this.useDeviceAuthLogin = !!config?.useDeviceAuthLogin;
        this.skipOpeningLogoutUrl = !!config?.skipOpeningLogoutUrl;
        this.starfleetUserSubscription = eventStreamFactory.createStream<StarfleetUser>(this.getCurrentUser.bind(this));
        this.isSameTabLoginFeatureEnabled = this.config.sameTabLogin;
        this.idTokenRefreshUnderDuration = this.config.idTokenRefreshUnderDuration ?? 0;
        this.storageType = this.config.storageType ?? StorageType.SharedStorage;
        if (this.idTokenRefreshUnderDuration < 0) {
            this.idTokenRefreshUnderDuration = 0;
        }

        if (this.storageType === StorageType.IndexedDB) {
            // Initialize IndexedDB
            this.globalStore = this.dbService.getGlobalStore(StarfleetStoreDetails.DBName);
        }
    }

    public getAuthStatus(): Observable<AuthStatus> {
        return this.authStatusSubject.asObservable();
    }

    // use cache = true, if no need to refresh session at initialize
    // If id_token is set, it will initialize with a passed-in identity
    public initialize(useCachedSession = false, useCachedUser = false, id_token?: string) {
        this.logger.info(`initializing session: ${useCachedSession}, user: ${useCachedUser}, id_token?: ${!!id_token}`);
        this.useCachedUser = useCachedUser;
        const authLoginData = this.oAuthService.readAndValidateoAuthLoginData();
        if (authLoginData) {
            this.attemptLoginAtInit(authLoginData);
        } else {
            this.loadSessionFromStorage()
                .pipe(
                    tap(() => this.initSub.next(true)),
                    mergeMap(() => this.getOrRefreshSession(!useCachedSession, true, id_token))
                )
                .subscribe(() => this.logger.info('initialized success'));
        }

        this.checkIfReturnFromLogout();

        this.deviceIdSub = this.getDeviceId();
        // get this to initialize
        this.deviceIdSub.subscribe();
        // initialize user
        this.accountStatusChanged.pipe(take(1)).subscribe();
    }

    private getDeviceId(): Observable<string> {
        return this.systemInfoService.getSystemInfo([SystemInfoKey.DeviceId]).pipe(
            map(systemInfo => systemInfo.DeviceId),
            first(),
            catchError(error => {
                this.logger.error('failed to get deviceId', error);
                return of('');
            }),
            shareReplay()
        );
    }

    private handleLoginError(error) {
        switch (error) {
            case AuthErrors.ALREADY_LOGGED_IN:
                this.logger.info('user already logged in');
                this.loginSubject.next(StarfleetTransitionState.LoginAbortedAlreadyLoggedIn);
                this.loginSubject.complete();
                break;

            case AuthErrors.AUTH_ERROR:
                this.logger.info('Auth error in login');
                this.loginSubject.next(StarfleetTransitionState.LoginFailed);
                this.loginSubject.complete();
                break;

            case AuthErrors.ACCOUNT_LOCKED:
            case AuthErrors.AGE_RESTRICTED:
                this.logger.info('Auth error in login: ', error);
                this.loginSubject.next(StarfleetTransitionState.LoginFailedNoRetry);
                this.loginSubject.complete();
                break;

            case AuthErrors.HTTP_START_FAILED:
                this.logger.info('Http Start in error Login');
                this.loginSubject.next(StarfleetTransitionState.LoginFailedToStart);
                this.loginSubject.complete();
                break;

            case AuthErrors.CLIENT_TOKEN_FAIL:
                this.logger.info('Client token url access failed');
                this.errorInfo.error_description = AuthErrors.CLIENT_TOKEN_FAIL;
                this.loginSubject.next(StarfleetTransitionState.LoginFailed);
                this.loginSubject.complete();
                break;

            case AuthErrors.OAUTH_DATA_EMPTY:
                this.logger.info('JSON parsing fialed for OAUTH Data');
                this.errorInfo.error_description = AuthErrors.OAUTH_DATA_EMPTY;
                this.loginSubject.next(StarfleetTransitionState.LoginFailed);
                this.loginSubject.complete();
                break;

            case AuthErrors.STARFLEET_REDIRECT_FAIL:
                this.logger.info('JSON parsing fialed for OAUTH Data');
                this.errorInfo.error_description = AuthErrors.STARFLEET_REDIRECT_FAIL;
                this.loginSubject.next(StarfleetTransitionState.LoginFailed);
                this.loginSubject.complete();
                break;

            case AuthErrors.LOGIN_CANCELLED_BY_USER:
                this.logger.info('canceled by user');
                this.errorInfo.error_description = AuthErrors.LOGIN_CANCELLED_BY_USER;
                this.loginSubject.next(StarfleetTransitionState.LoginAbortedByUser);
                this.loginSubject.complete();
                break;

            default:
                this.logger.info('unknown error in login', error);
                this.loginSubject.next(StarfleetTransitionState.LoginFailed);
                this.loginSubject.complete();
                break;
        }
    }

    /**
     * cancels any subscriptions created during login and listens for user cancelled error
     * The boolean argument decides whether the server should be stopped directly or whether it should
     * listening for error
     */
    public cancelLogin(stopHttpServer: boolean = true): Observable<OAuthLoginData> {
        this.logger.info('cancelling login');
        if (this.loginSubject && this.loginSubject.isStopped === false) {
            this.loginSubject.next(StarfleetTransitionState.LoginAbortedByUser);
            this.loginSubject.complete();
        }
        if (this.loginSubscription) {
            this.loginSubscription.unsubscribe();
            this.loginSubscription = null;
        }
        return this.oAuthService.cancelOAuthLogin(this.config, this.localeService.getLocale(), stopHttpServer);
    }

    public sameTabAuthStarted(): Observable<StarfleetTransitionState> {
        return this.oAuthService.sameTabAuthStarted();
    }

    /**
     * Calls the OAuth service and subscribes for the access code which is emitted upon a successful login
     */
    public login(options: LoginOptions): Observable<StarfleetTransitionState> {
        if (!this.loginSubject || this.loginSubject.isStopped) {
            this.loginSubject = new ReplaySubject<StarfleetTransitionState>(1);
        } else {
            return throwError(StarfleetTransitionState.LoginAlreadyInProgress);
        }

        this.logger.info('login started');
        // Indicate a login transition is starting with a url being opened in browser.
        this.loginSubject.next(StarfleetTransitionState.LoginStarted);

        this.loginSubscription = this.getOrRefreshSession()
            .pipe(
                mergeMap(session => {
                    if (!options.force && session.isAccessTokenValid()) {
                        return throwError(AuthErrors.ALREADY_LOGGED_IN);
                    }
                    return this.deviceIdSub;
                }),
                mergeMap((deviceId: string) => {
                    if (this.useDeviceAuthLogin) {
                        if (!options.deviceName) {
                            this.logger.error(
                                'Missing device name in off-device login flow, falling back to default value'
                            );
                            options.deviceName = 'unknown';
                        }
                        return this.performDeviceLogin(deviceId, options.deviceName, options.idpId);
                    }
                    this.loginSubject.next(StarfleetTransitionState.OpenOAuthUrlStarted);
                    return this.oAuthService.oAuthLogin(
                        this.config,
                        this.localeService.getLocale(),
                        deviceId,
                        this.loginSubject,
                        options.idpId,
                        options.preCreatedWindow,
                        options.returnLocationUrl,
                        options.silentLoginInfo,
                        options.redirect
                    );
                }),
                first(),
                mergeMap((authLoginData: OAuthLoginData) => {
                    // to do fix this error
                    if (!authLoginData || isEmpty(authLoginData)) {
                        this.logger.info('received empty oauth login data ', this.isSameTabLoginFeatureEnabled);
                        return of(null);
                    }
                    this.loginSubject.next(StarfleetTransitionState.LoginRedirectCompleted);
                    this.logger.info('OAuth login complete, starting to retrive token');
                    this.loginSubject.next(StarfleetTransitionState.LoginStarted);
                    return this.attemptLogin(authLoginData);
                }),
                mergeMap(session => {
                    if (session) {
                        return this.refreshUser();
                    } else {
                        return of(session);
                    }
                }),
                tap(session => {
                    if (session) {
                        this.loginSubject.next(StarfleetTransitionState.LoginCompleted);
                    } else if (!this.isSameTabLoginFeatureEnabled) {
                        // in same tab it is expected that data will be null/empty
                        this.logger.error('session is null');
                    }
                    this.loginSubject.complete();
                })
            )
            .subscribe(noop, this.handleLoginError.bind(this));

        return this.loginSubject.asObservable();
    }

    public updatePiplConfigUrls(piplConfigData: BaseConfigType): void {
        this.logger.info('Updating PIPL config urls: ', piplConfigData);
        Object.assign(this.config, piplConfigData);
    }

    private checkIfReturnFromLogout() {
        let errorDetails = null;
        let logoutNotSupportedRedirectUrl;
        if (window.sessionStorage.getItem(markLogoutInitiateKey) === markLogoutInitiateVal) {
            window.sessionStorage.removeItem(markLogoutInitiateKey);
            this.logger.info('Returning from logout');
            const err = new URL(window.location.href).searchParams.get('error');
            logoutNotSupportedRedirectUrl = window.sessionStorage.getItem(logoutNotSupportedUrlKey);
            window.sessionStorage.removeItem(logoutNotSupportedUrlKey);
            if (err) {
                this.logger.info('Logout failed with error ', err);
                errorDetails =
                    err === AuthErrors.LOGOUT_NOT_SUPPORTED
                        ? AuthErrors.LOGOUT_NOT_SUPPORTED
                        : AuthErrors.LOGOUT_GENERIC_ERROR;
            }
            const authStatus: AuthStatus = {
                authReturnState: StarfleetTransitionState.LogoutCompleted,
                error: errorDetails,
                logoutNotSupportedRedirectUrl
            };
            this.authStatusSubject.next(authStatus);
        } else {
            this.logger.info('Not returning from logout');
        }
    }

    private completeLoginAtInit(loginState: StarfleetTransitionState, err) {
        if (loginState === StarfleetTransitionState.LoginFailed) {
            this.session = new Session(this.idTokenRefreshUnderDuration, null, this.storageType);
        }
        this.initSub.next(true);

        const authStatus: AuthStatus = {
            authReturnState: loginState,
            error: err
        };
        this.authStatusSubject.next(authStatus);

        this.refreshUser();
        this.loginSubject.next(loginState);
        this.loginSubject.complete();
    }

    private attemptLoginAtInit(authLoginData: OAuthLoginData): void {
        if (!this.loginSubject || this.loginSubject.isStopped) {
            this.loginSubject = new ReplaySubject<StarfleetTransitionState>(1);
        } else {
            this.logger.info('Login already in progress');
            return;
        }

        this.logger.info('OAuth login complete, starting to retrive token');
        this.loginSubject.next(StarfleetTransitionState.LoginStarted);

        this.attemptLogin(authLoginData)
            .pipe(
                tap(
                    _ => {
                        this.completeLoginAtInit(StarfleetTransitionState.LoginCompleted, null);
                    },
                    err => {
                        this.completeLoginAtInit(StarfleetTransitionState.LoginFailed, err);
                    }
                )
            )
            .subscribe();
    }

    private retryDeviceLogin(max_retries: number, retry_interval: number, attempts: Observable<any>) {
        return attempts.pipe(
            mergeMap((error, i) => {
                const attemptNumber = i + 1;
                console.log('attempt number: ', attemptNumber);
                if (attemptNumber > max_retries) {
                    // TODO: could make this throw a custom timeout error to display a different message?
                    return throwError(error);
                }

                // not sure about this?
                if (error?.rawResponse?.error?.error === 'authorization_pending') {
                    return timer(retry_interval * 1000);
                } else if (error?.rawResponse?.error?.error === 'slow_down') {
                    return timer((retry_interval + 5) * 1000);
                }
                return throwError(error);
            })
        );
    }

    private waitForDeviceLogin(authResponse: DeviceAuthorizeResponse): Observable<OAuthLoginData> {
        const number_of_retries = Math.max(1, Math.floor(authResponse.expires_in / authResponse.interval) - 1);

        return timer(authResponse.interval * 1000).pipe(
            mergeMap(() => this.starfleetHttpService.getAccessTokenFromDeviceCode(authResponse.device_code)),
            retryWhen(errors => this.retryDeviceLogin(number_of_retries, authResponse.interval, errors)),
            map((accessTokenResponse: AccessTokenResponse) => {
                const authLoginData: OAuthLoginData = {
                    error: '',
                    error_description: '',
                    access_token: accessTokenResponse.access_token,
                    token_type: accessTokenResponse.token_type,
                    expires_in: accessTokenResponse.expires_in,
                    id_token: accessTokenResponse.id_token
                };
                return authLoginData;
            })
        );
    }

    private performDeviceLogin(deviceId: string, deviceName: string, idpId?: string): Observable<OAuthLoginData> {
        return this.starfleetHttpService.requestDeviceAuthorize(deviceId, deviceName, idpId).pipe(
            first(),
            mergeMap((deviceAuthData: DeviceAuthorizeResponse) => {
                this.deviceDisplayCode = {
                    url: deviceAuthData.verification_uri,
                    url_complete: deviceAuthData.verification_uri_complete,
                    user_code: deviceAuthData.user_code
                };
                this.loginSubject.next(StarfleetTransitionState.LoginInProgressNeedDisplayCode);

                // sanity check that the retry interval is set
                if (!deviceAuthData.interval || deviceAuthData.interval <= 0) {
                    deviceAuthData.interval = 5;
                }
                return this.waitForDeviceLogin(deviceAuthData);
            })
        );
    }

    private getStarfleetLogoutURL(idToken: string, locale: string): string {
        return STARFLEET_LOGOUT_URL_TEMPLATE.replace(':server', this.config.url)
            .replace(':idToken', idToken)
            .replace(':locale', locale);
    }

    private getStarfleetLogoutURLSameTab(idToken: string, locale: string, returnLocationUrl: string): string {
        return STARFLEET_LOGOUT_URL_TEMPLATE_SAME_TAB.replace(':server', this.config.logoutUrl)
            .replace(':idToken', idToken)
            .replace(':locale', locale)
            .replace(':redirect_uri', returnLocationUrl);
    }

    private saveRequiredDataAndOpenLogoutUrl(
        idToken: string,
        logOutNotSupportedUrl: string,
        returnLocationUrl: string
    ) {
        this.oAuthService.sameTabAuthStarted().subscribe((evt: StarfleetTransitionState) => {
            this.logger.info('Received notification for ', evt);
            window.sessionStorage.setItem(markLogoutInitiateKey, markLogoutInitiateVal);
            window.sessionStorage.setItem(logoutNotSupportedUrlKey, logOutNotSupportedUrl);
            window.open(
                this.getStarfleetLogoutURLSameTab(idToken, this.localeService.getLocale(), returnLocationUrl),
                '_self'
            );
        });
        this.oAuthService.emitAuthStartNotification(StarfleetTransitionState.LogoutStarted);
        return of(true);
    }

    /**
     * Log out from the currently connected starfleet account.
     */
    public logout(
        accessToken?: string,
        logOutNotSupportedUrl?: string,
        returnLocationUrl?: string,
        isSilentLogout = false
    ): Observable<StarfleetTransitionState> {
        if (this.session.isClientTokenValid() === false) {
            this.logger.info('already logged out, completing the logout process');
            return of(StarfleetTransitionState.LogoutCompleted);
        }

        if (!this.logoutSubject || this.logoutSubject.isStopped) {
            this.logoutSubject = new ReplaySubject<StarfleetTransitionState>(1);
        } else {
            this.logger.info('logout already in progress');
            return this.logoutSubject.asObservable();
        }

        this.logger.info('Logging out user.');
        this.logoutSubject.next(StarfleetTransitionState.LogoutStarted);
        const idToken = this.session?.data?.idToken;
        const idpId = this.session.data.user.idp_id;

        // accessToken is defined when user cancels the login flow for native client.
        // don't open a new browser tab for this case as logout should happen in the background.
        const openLogoutUrlInNewTab =
            !accessToken && !this.skipOpeningLogoutUrl && !this.isSameTabLoginFeatureEnabled && !isSilentLogout;

        if (openLogoutUrlInNewTab) {
            const logOutUrl = this.getStarfleetLogoutURL(idToken, this.localeService.getLocale());
            if (this.config.useNativeAuthCommands) {
                this.bridgeService
                    .executeCommand(Commands.Common.Nativeauth.initiateLogOut, { url: logOutUrl })
                    .pipe(take(1))
                    .subscribe();
            } else {
                // mobile Safari is more sensitive on response time between user gesture and calling of window.open.
                // window.open needs to be called as early as possible. otherwise it will be rejected/ignored.
                window.open(logOutUrl, '_blank');
            }
        }

        this.deleteClientToken(false, accessToken)
            .pipe(
                catchError(err => {
                    this.logger.error('delete token failed with error: ', err);
                    return of(null);
                }),
                mergeMap(() =>
                    this.storeSession(new Session(this.idTokenRefreshUnderDuration, null, this.storageType))
                ),
                mergeMap(() => {
                    if (!this.skipOpeningLogoutUrl && !isSilentLogout && this.isSameTabLoginFeatureEnabled) {
                        return this.saveRequiredDataAndOpenLogoutUrl(idToken, logOutNotSupportedUrl, returnLocationUrl);
                    } else {
                        return this.refreshUser();
                    }
                })
            )
            .subscribe(
                () => {
                    this.logger.info('log out complete.');
                    this.logoutSubject.next(StarfleetTransitionState.LogoutCompleted);
                    this.logoutSubject.complete();
                },
                error => {
                    this.logger.error('log out failed with error:', error);
                    this.logoutSubject.next(StarfleetTransitionState.LogoutFailed);
                    this.logoutSubject.complete();
                    if (!this.skipOpeningLogoutUrl && !isSilentLogout && this.isSameTabLoginFeatureEnabled) {
                        this.saveRequiredDataAndOpenLogoutUrl(idToken, logOutNotSupportedUrl, returnLocationUrl);
                    } else {
                        this.refreshUser();
                    }
                }
            );
        return this.logoutSubject.asObservable();
    }

    /**
     * Calls delete client token API and handles 401 error.
     *
     * @param accessToken
     * @returns
     */
    private deleteClientToken(refreshForExpired = false, inAccessToken?: string) {
        return (inAccessToken ? of(inAccessToken) : this.getAccessToken(refreshForExpired)).pipe(
            mergeMap(accessToken => this.starfleetHttpService.deleteClientTokens(accessToken)),
            catchError(err => {
                this.logger.error('Got error while deleting client token.', err);
                return this.handleAuthError<any>(err, refreshForExpired, this.deleteClientToken.bind(this));
            })
        );
    }

    /**
     * Requests a fresh StarfleetUser object from Starfleet. Subscribers of
     * the starfleetUserSubscription will also be notified when data is
     * returned.
     */
    public refreshUser(): Observable<StarfleetUser> {
        this.firstRefreshDone = true;
        return this.starfleetUserSubscription.refresh();
    }

    // Events
    /**
     * Subscribe to receive events when login status has changed.
     *
     * @returns Returns a starfleet user if logged in or undefined if logged out
     *
     * Usage:
     * starfleetService.accountStatusChanged.subscribe(...);
     */
    public get accountStatusChanged(): Observable<StarfleetUser> {
        let trackedStarfleetUser: StarfleetUser;
        return this.starfleetUserSubscription.get$().pipe(
            filter(
                (starfleetUser: StarfleetUser) =>
                    isNil(trackedStarfleetUser) ||
                    !isEqual(trackedStarfleetUser.sub, starfleetUser.sub) ||
                    !isEqual(
                        trackedStarfleetUser.consent?.trackBehavioralData,
                        starfleetUser.consent?.trackBehavioralData
                    ) ||
                    !isEqual(
                        trackedStarfleetUser.consent?.trackTechnicalData,
                        starfleetUser.consent?.trackTechnicalData
                    ) ||
                    !isEqual(trackedStarfleetUser.consent?.targetedAdsOptIn, starfleetUser.consent?.targetedAdsOptIn) ||
                    !isNil(starfleetUser?.age)
            ),
            tap((starfleetUser: StarfleetUser) => {
                trackedStarfleetUser = starfleetUser;
            })
        );
    }

    public getStarfleetSession(): Observable<Session> {
        return this.sessionSubject.asObservable();
    }
    /**
     * Get whether user token is available in  storage
     *
     * @returns true/false based on whether user token is available inside the stored session object.
     */
    public get isUserLoggedIn(): Observable<boolean> {
        return this.initSub.pipe(map(() => this.session.isClientTokenValid()));
    }

    /**
     * Takes the access token and retrieves user info, client token
     *
     * @param authLoginData: is returned from OAuth window
     */
    private attemptLogin(authLoginData: OAuthLoginData): Observable<Session> {
        this.errorInfo.error_code = authLoginData.error ? authLoginData.error : '';
        this.errorInfo.error_description = authLoginData.error_description ? authLoginData.error_description : '';
        this.errorInfo.error_uri = authLoginData.error_uri ? authLoginData.error_uri : '';

        if (authLoginData.error || authLoginData.error_description) {
            this.logger.error('failed to login user with error', authLoginData.error, authLoginData.error_description);
            return throwError(authLoginData.error);
        }

        let tokenSub: Observable<OAuthLoginData> = null;
        if (authLoginData.code && authLoginData.oAuthUrl?.codeVerifier) {
            // Authorization code flow with PKCE
            tokenSub = this.starfleetHttpService
                .redeemAuthCode(
                    authLoginData.code,
                    authLoginData.oAuthUrl.redirectUrl,
                    authLoginData.oAuthUrl.codeVerifier
                )
                .pipe(
                    map(response => {
                        authLoginData.access_token = response.access_token;
                        authLoginData.id_token = response.id_token;
                        authLoginData.expires_in = response.expires_in;
                        authLoginData.token_type = response.token_type;
                        return authLoginData;
                    }),
                    catchError(err => {
                        this.logger.error('error in redeeming auth code', err);
                        throw AuthErrors.REDEEM_AUTHCODE_FAILED;
                    })
                );
        } else {
            // implicit flow
            tokenSub = of(authLoginData);
        }

        return tokenSub.pipe(mergeMap(_ => this.completeLogin(authLoginData)));
    }

    private validateRedirectResponse(authLoginData: OAuthLoginData): boolean {
        if (this.useDeviceAuthLogin) {
            return true; // Nonce is not used in device Auth  login
        }

        const idTokenData = this.parseIdToken(authLoginData.id_token);
        if (idTokenData && idTokenData.nonce !== authLoginData.oAuthUrl.nonce) {
            throw AuthErrors.NONCE_VALIDATION_FAILED;
        }

        return true;
    }

    private completeLogin(authLoginData: OAuthLoginData): Observable<Session> {
        const sessionObj: Session = new Session(this.idTokenRefreshUnderDuration, null, this.storageType);
        return this.starfleetHttpService.getClientToken(authLoginData.access_token).pipe(
            catchError(error => {
                this.logger.error('Client Token url failure ', error);
                throw AuthErrors.CLIENT_TOKEN_FAIL;
            }),
            map((data: ClientTokenResponse) => {
                sessionObj.data.accessToken = authLoginData.access_token;
                sessionObj.data.accessTokenExpiry = Date.now() + authLoginData.expires_in * 1000;
                sessionObj.data.idToken = authLoginData.id_token;
                sessionObj.data.clientToken = data.client_token;
                sessionObj.data.clientTokenExpiry = Date.now() + data.expires_in * 1000;
                sessionObj.data.clientTokenExpiryLength = data.expires_in * 1000;
                sessionObj.data.idTokenExpiry = this.getIdTokenExpiry(sessionObj.data.idToken);
                this.session = sessionObj;
                return this.session;
            })
        );
    }

    public syncSession(externalSession: Session): Observable<Session> {
        if (externalSession?.isClientTokenValid()) {
            if (!this.session?.isClientTokenValid()) {
                // User was logged in natively, but we are not logged in.
                return this.migrateUser(externalSession);
            } else {
                // User is logged in natively, and we are logged in.
                return this.updateUserTokens(externalSession);
            }
        }
        return of(this.session);
    }

    private migrateUser(inSession: Session): Observable<Session> {
        this.session = inSession;
        this.logger.info('migrateUser');
        return this.refreshUser().pipe(map((user: StarfleetUser) => this.session));
    }

    private updateUserTokens(fromSession: Session): Observable<Session> {
        this.session.data.idTokenExpiry = fromSession.data.idTokenExpiry;
        this.session.data.clientTokenExpiry = fromSession.data.clientTokenExpiry;
        this.session.data.accessTokenExpiry = fromSession.data.accessTokenExpiry;
        this.session.data.idToken = fromSession.data.idToken;
        this.session.data.clientToken = fromSession.data.clientToken;
        this.session.data.accessToken = fromSession.data.accessToken;
        return of(this.session);
    }

    private getIdTokenExpiry(idToken: string): number {
        let expiry = 0;
        const payload = this.parseIdToken(idToken);
        if (payload) {
            expiry = Date.now() + (+payload.exp - +payload.iat) * 1000;
        }

        return expiry;
    }

    private parseIdToken(idToken: string): IdTokenData {
        try {
            const idTokenPayloadB64url = idToken.split('.')[1];
            const idTokenPayloadB64 = idTokenPayloadB64url.replace(/-/g, '+').replace(/_/g, '/');
            const payload = JSON.parse(decodeURIComponent(escape(atob(idTokenPayloadB64))));
            return payload;
        } catch (err) {
            this.errorHandler.handleError(new Error(`idToken parse failed with error '${err}'`));
            this.logger.error('failed to parse idToken', err);
            return null;
        }
    }

    private isClientTokenRefreshRequired() {
        // If client token expiry time is less than clientTokenRefreshDuration [currently, 27 days (i.e 2332800000  milliseconds) ],
        // or clientTokenRefreshDurationInPercentage [ currently, 30% ] of expires_in field then, renew client token and update session object
        let requireClientTokenRefresh = false;
        if (this.session.isClientTokenValid()) {
            const clientTokenExpiresIn = this.session.data.clientTokenExpiry - Date.now();
            if (this.session.data.clientTokenExpiryLength) {
                requireClientTokenRefresh =
                    clientTokenExpiresIn <
                    (this.config.clientTokenRefreshDurationInPercentage / 100) *
                        this.session.data.clientTokenExpiryLength;
            } else {
                requireClientTokenRefresh = clientTokenExpiresIn < this.config.clientTokenRefreshDuration;
            }
        }

        return requireClientTokenRefresh;
    }

    /**
     * This fetchs a new access token from the server
     *
     * @returns Observable<Session>
     */
    private fetchSession(noRefreshUser?: boolean, parentSpan?: OpenTelemetrySpan): Observable<Session> {
        return this.starfleetHttpService
            .getAccessToken(this.session.data.clientToken, this.session.data.user.sub, parentSpan)
            .pipe(
                mergeMap((response: AccessTokenResponse) => {
                    this.session.data.accessToken = response.access_token;
                    this.session.data.clientToken = response.client_token;
                    this.session.data.idToken = response.id_token;
                    this.session.data.accessTokenExpiry = Date.now() + response.expires_in * 1000;
                    this.session.data.idTokenExpiry = this.getIdTokenExpiry(this.session.data.idToken);
                    this.isSessionRefreshable = true;
                    return of(this.session);
                }),
                mergeMap(() => {
                    const oldClientTokenExpiresIn = this.session.data.clientTokenExpiry - Date.now();
                    if (this.isClientTokenRefreshRequired() && this.isNextClientTokenRefreshAllowed) {
                        this.logger.info('Refreshing Client Token');
                        return this.starfleetHttpService.getClientToken(this.session.data.accessToken, parentSpan).pipe(
                            map((data: ClientTokenResponse) => {
                                this.logger.info('Client Token refreshed Successfully');
                                this.session.data.clientToken = data.client_token;
                                this.session.data.clientTokenExpiry = Date.now() + data.expires_in * 1000;
                                // store the expires_in field as well for clientToken
                                this.session.data.clientTokenExpiryLength = data.expires_in * 1000;

                                this.isNextClientTokenRefreshAllowed =
                                    oldClientTokenExpiresIn < this.session.data.clientTokenExpiryLength;
                                return this.session;
                            })
                        );
                    } else {
                        return of(this.session);
                    }
                }),
                catchError(error => {
                    this.logger.info('Error refreshing access token with starfleet:', error);
                    if (isTimeoutOrConnectivityError(error)) {
                        this.logger.info(
                            'The session refresh failed due to timeout or connection issues. So, will not log out the user'
                        );
                        return of(this.session);
                    }
                    this.logger.info('Error thrown from server. Hence, logging the user out');
                    return this.storeSession(new Session(this.idTokenRefreshUnderDuration, null, this.storageType));
                }),
                finalize(() => {
                    if (!noRefreshUser) {
                        this.refreshUser();
                    }
                })
            );
    }

    /**
     * Return current logged in user or UnAuthenticatedUser
     * This verifies that session token is still valid. It tried to renew session if the session has expired.
     * Otherwise returns unAuthentictedUser
     *
     * @returns Observable<StarfleetUSer>
     */
    private getCurrentUser(refreshForExpired = false): Observable<StarfleetUser> {
        return this.getOrRefreshSession(refreshForExpired, true).pipe(
            mergeMap((session: Session) => {
                this.logger.info('Getting current user');
                if (this.useCachedUser) {
                    this.useCachedUser = false;
                    return throwError('using cached user at initialize');
                }
                if (!this.networkStatus.isOnline && session.isClientTokenValid()) {
                    this.logger.info('User is offline and session has client token. So returning cached user');
                    return throwError(AuthErrors.USER_OFFLINE);
                }
                if (session.isAccessTokenValid()) {
                    if (session.data.user && !this.firstRefreshDone) {
                        this.firstRefreshDone = true;
                        this.refreshUser();
                        return throwError('using deferred refresh for user');
                    }
                    // Get the user profile info using the sessionToken ensures that we still have a valid session for this user.
                    return this.starfleetHttpService.getUserInfo(session.data.accessToken).pipe(
                        map((user: StarfleetUser) => {
                            user.isNetworkCall = true;
                            return user;
                        })
                    );
                }

                return throwError(AuthErrors.NOT_LOGGED_IN);
            }),
            mergeMap((user: StarfleetUser) => {
                user['isAuthenticated'] = true;
                this.session.data.user = user;
                return this.storeSession(this.session);
            }),
            map(() => this.session.data.user),
            catchError(error =>
                this.handleAuthError<StarfleetUser>(error, refreshForExpired, this.getCurrentUser.bind(this))
            ),
            catchError(error => {
                this.logger.error('Error fetching userInfo from starfleet:', error.toString());
                if (isTimeoutOrConnectivityError(error)) {
                    this.logger.info('UserInfo refresh failed due to timeout or connection issue');
                }
                return of(this.session.data.user);
            })
        );
    }

    public createNewSessionFromIdToken(id_token: string): Observable<Session> {
        return this.getOrRefreshSession(false, false, id_token).pipe(tap(() => this.refreshUser()));
    }

    private getOrRefreshSession(
        forceRefresh?: boolean,
        noRefreshUser?: boolean,
        id_token?: string,
        parentSpan?: OpenTelemetrySpan
    ): Observable<Session> {
        if (this.sessionSubject && this.sessionSubject.isStopped === false) {
            return this.sessionSubject.asObservable();
        } else {
            this.sessionSubject = new ReplaySubject<Session>(1);
        }
        this.initSub
            .pipe(
                mergeMap(() => {
                    const isClientTokenValid = this.session.isClientTokenValid();
                    if (
                        (forceRefresh || !this.session.isAccessTokenValid()) &&
                        isClientTokenValid &&
                        this.networkStatus.isOnline &&
                        this.isSessionRefreshable
                    ) {
                        this.logger.info('refreshing session. Force Refresh: ', forceRefresh);
                        return this.fetchSession(noRefreshUser, parentSpan);
                    }

                    if (!isClientTokenValid) {
                        if (id_token && id_token.length !== 0) {
                            return this.createSessionFromIdToken(id_token);
                        } else {
                            // user is logged out, reload from  storage
                            this.logger.info(`Loading session from  ${this.storageType}`);
                            return this.loadSessionFromStorage();
                        }
                    }

                    this.logger.info('Using existing client token ');
                    return of(this.session);
                })
            )
            .subscribe(
                session => {
                    this.sessionSubject.next(session);
                    this.sessionSubject.complete();
                },
                error => {
                    this.sessionSubject.error(error);
                    this.logger.error('failed to refersh session', error);
                }
            );

        return this.sessionSubject.asObservable();
    }

    private createSessionFromIdToken(id_token: string): Observable<Session> {
        this.logger.info('Building session from sdk id token');
        let userData: StarfleetUser;
        try {
            let payload: any = id_token.split('.')[1];
            payload = JSON.parse(window.atob(payload));
            userData = {
                sub: payload.sub,
                idp_name: payload.idp_name,
                idp_id: payload.idp_id,
                external_id: payload.external_id,
                preferred_username: payload.preferred_username,
                consent: payload.consent,
                isAuthenticated: true
            };
        } catch (err) {
            this.logger.error('Failed to parse id token to get expiry');
            this.logger.info(`Loading session from  ${this.storageType}`);
            return this.loadSessionFromStorage();
        }
        const sessionData: SessionData = {
            clientToken: 'This is required for the session object to validate, but is not used',
            accessToken: 'This is required for the session object to validate, but is not used',
            idToken: id_token,
            user: userData,
            // Using dummy value two days from now
            clientTokenExpiry: Date.now() + 1000 * 60 * 60 * 48,
            accessTokenExpiry: Date.now() + 1000 * 60 * 60 * 48,
            idTokenExpiry: this.getIdTokenExpiry(id_token)
        };
        // This prevents attempts to refresh the session from overwriting a session that lacks an accessToken/clientToken
        this.isSessionRefreshable = false;
        this.session = new Session(this.idTokenRefreshUnderDuration, sessionData, this.storageType);
        return of(this.session);
    }

    private loadSessionFromStorage(): Observable<Session> {
        const storage = this.storageType === StorageType.SharedStorage ? this.sharedStorage : this.globalStore;
        return Session.loadFrom(storage, this.idTokenRefreshUnderDuration).pipe(
            mergeMap((session: Session) => {
                this.logger.info(`Loaded session successfully from ${this.storageType}`);
                this.session = session;
                if (session.isClientTokenValid()) {
                    return of(session);
                }
                this.logger.info('Stored session does not have user token. Hence, logging the user out');
                this.session = new Session(this.idTokenRefreshUnderDuration, null, this.storageType);
                this.isSessionRefreshable = true;
                return of(this.session);
            }),
            catchError(error => {
                this.logger.info(`Error loading session from ${this.storageType}. Hence logging out the user.`, error);
                this.session = new Session(this.idTokenRefreshUnderDuration, null, this.storageType);
                return of(this.session);
            })
        );
    }

    private storeSession(session: Session): Observable<Session> {
        this.session = cloneDeep(session);
        delete session.data.user?.age;
        delete session.data.user?.isNetworkCall;
        const storage = this.storageType === StorageType.SharedStorage ? this.sharedStorage : this.globalStore;
        return session.writeTo(storage).pipe(
            mergeMap(() => of(session)),
            catchError(error => {
                this.logger.info(`Error saving session to ${this.storageType}:`, error);
                return throwError(error);
            })
        );
    }

    public getAuthToken(refreshForExpired?: boolean, parentSpan?: OpenTelemetrySpan): Observable<string> {
        return this.initSub.pipe(
            mergeMap(_ => {
                if (refreshForExpired || !this.session.isIdTokenValid()) {
                    if (this.session.hasClientTokenExpired()) {
                        this.refreshUser();
                        return of(new Session(this.idTokenRefreshUnderDuration, null, this.storageType));
                    }
                    return this.getOrRefreshSession(true, null, null, parentSpan);
                } else {
                    return of(this.session);
                }
            }),
            map(session => session.data.idToken)
        );
    }

    private getAccessToken(refreshForExpired?: boolean, parentSpan?: OpenTelemetrySpan): Observable<string> {
        return this.initSub.pipe(
            mergeMap(_ => {
                if (refreshForExpired || !this.session.isAccessTokenValid()) {
                    return this.getOrRefreshSession(true, null, null, parentSpan);
                } else {
                    return of(this.session);
                }
            }),
            map(session => session.data.accessToken)
        );
    }

    public getClientData(
        keys?: string[],
        parentSpan?: OpenTelemetrySpan,
        refreshForExpired = false
    ): Observable<KVStoreClientDataResponse> {
        return this.getAuthToken(refreshForExpired, parentSpan).pipe(
            mergeMap(authToken => this.starfleetHttpService.getClientData(authToken, keys, parentSpan)),
            catchError(error =>
                this.handleAuthError<KVStoreClientDataResponse>(
                    error,
                    refreshForExpired,
                    this.getClientData.bind(this),
                    keys
                )
            )
        );
    }

    public setClientData(
        key: string,
        value: string[],
        parentSpan?: OpenTelemetrySpan,
        refreshForExpired = false
    ): Observable<any> {
        return this.getAuthToken(refreshForExpired, parentSpan).pipe(
            mergeMap(authToken => this.starfleetHttpService.setClientData(authToken, key, value, parentSpan)),
            catchError(error =>
                this.handleAuthError<any>(error, refreshForExpired, this.setClientData.bind(this), key, value)
            )
        );
    }

    public appendClientData(
        key: string,
        value: string[],
        parentSpan?: OpenTelemetrySpan,
        refreshForExpired = false
    ): Observable<any> {
        return this.getAuthToken(refreshForExpired, parentSpan).pipe(
            mergeMap(authToken => this.starfleetHttpService.appendClientData(authToken, key, value, parentSpan)),
            catchError(error =>
                this.handleAuthError<any>(error, refreshForExpired, this.appendClientData.bind(this), key, value)
            )
        );
    }

    public deleteClientData(
        key: string,
        value?: string[],
        parentSpan?: OpenTelemetrySpan,
        refreshForExpired = false
    ): Observable<any> {
        return this.getAuthToken(refreshForExpired, parentSpan).pipe(
            mergeMap(authToken => this.starfleetHttpService.deleteClientData(authToken, key, value, parentSpan)),
            catchError(error =>
                this.handleAuthError<any>(error, refreshForExpired, this.deleteClientData.bind(this), key)
            )
        );
    }

    public handleAuthError<T>(
        error,
        refreshForExpired: boolean,
        callBackforTokenExpired: (...args) => Observable<T>,
        ...args
    ): Observable<T> {
        if (error.status === 401) {
            if (!refreshForExpired) {
                this.logger.info('refershing auth token for fn: ', callBackforTokenExpired?.name);
                args.push(true);
                return callBackforTokenExpired(...args);
            } else {
                return throwError(AuthErrors.NOT_LOGGED_IN);
            }
        } else {
            return throwError(error);
        }
    }

    public get lastError(): ErrorInfo {
        return this.errorInfo;
    }

    public getDeviceDisplayCode(): DeviceDisplayCodeData {
        return this.deviceDisplayCode;
    }

    public cancel() {
        if (this.loginSubject?.isStopped === false) {
            this.loginSubject.complete();
        }
        this.loginSubscription?.unsubscribe();
        this.loginSubscription = null;
    }
}
